import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ClipboardList, ArrowUpDown, Printer, Trash2, GripVertical, Bookmark, ChevronRight,
} from "lucide-react";
import axios from "axios";
import { CutListItem } from "./CutListItem";
import { speciesOrFirstWord } from "@/lib/species";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function formatTime(minutes) {
  if (minutes < 1) {
    return `${Math.round(minutes * 60)} seconds`;
  }
  if (minutes >= 60) {
    const hrs = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return mins > 0 ? `${hrs.toLocaleString()}h ${mins}m` : `${hrs.toLocaleString()}h`;
  }
  return `${minutes.toFixed(1)} min`;
}

export function SavedList({
  savedList, dragIdx, toggleCheck, removeFromList, moveItem,
  sortByThickness, setClearListConfirm, updateItemCustomQty, updateItemStopwatch,
  toggleSavedForLater, moveToHistory, readOnly,
  listTitle, listId, collapsible, onShowHistory,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [histChooser, setHistChooser] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [customQty, setCustomQty] = useState("");
  const [editingTimeId, setEditingTimeId] = useState(null);
  const [customTime, setCustomTime] = useState("");
  const [tick, setTick] = useState(0);
  const [sortConfirm, setSortConfirm] = useState(false);
  const [printDialog, setPrintDialog] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  const [priceBdFtRaw, setPriceBdFtRaw] = useState(0);
  const [priceHourRaw, setPriceHourRaw] = useState(0);
  const [bdFtSource, setBdFtSource] = useState("checked");
  const [timeSource, setTimeSource] = useState("actual");
  const [customBdFt, setCustomBdFt] = useState("");
  const [customHours, setCustomHours] = useState("");
  const [highlightOn, setHighlightOn] = useState(localStorage.getItem('highlight_bar') === 'on');

  useEffect(() => {
    const onChange = () => setHighlightOn(localStorage.getItem('highlight_bar') === 'on');
    window.addEventListener('highlight-changed', onChange);
    return () => window.removeEventListener('highlight-changed', onChange);
  }, []);

  // Memoize whether any timer is running to avoid recalculating on every render
  const hasRunning = useMemo(() => savedList.some(i => i.stopwatch_started_at), [savedList]);

  // Single tick interval to update display for running timers
  useEffect(() => {
    if (hasRunning) {
      const id = setInterval(() => setTick(t => t + 1), 1000);
      return () => clearInterval(id);
    }
  }, [hasRunning]);

  const MAX_SECONDS = 60 * 3600; // 60 hours

  const getElapsed = useCallback((item) => {
    const base = item.stopwatch_elapsed || 0;
    if (item.stopwatch_started_at) {
      const total = base + Math.floor((Date.now() - new Date(item.stopwatch_started_at).getTime()) / 1000);
      return Math.min(total, MAX_SECONDS);
    }
    return Math.min(base, MAX_SECONDS);
  }, []);

  // Auto-stop timers that hit 60 hours
  useEffect(() => {
    savedList.forEach(item => {
      if (item.stopwatch_started_at && getElapsed(item) >= MAX_SECONDS) {
        updateItemStopwatch(item.id, { stopwatch_elapsed: MAX_SECONDS, stopwatch_started_at: null });
      }
    });
  });

  const startTimer = useCallback((id) => {
    const item = savedList.find(i => i.id === id);
    const elapsed = item ? (item.stopwatch_elapsed || 0) : 0;
    const fields = { stopwatch_started_at: new Date().toISOString(), stopwatch_elapsed: elapsed };
    if (!item?.cut_date) fields.cut_date = new Date().toLocaleDateString('en-CA');
    updateItemStopwatch(id, fields);
  }, [savedList, updateItemStopwatch]);

  const stopTimer = useCallback((id) => {
    const item = savedList.find(i => i.id === id);
    if (!item) return;
    const elapsed = getElapsed(item);
    updateItemStopwatch(id, { stopwatch_elapsed: elapsed, stopwatch_started_at: null });
  }, [savedList, getElapsed, updateItemStopwatch]);

  const resetTimer = useCallback((id) => {
    updateItemStopwatch(id, { stopwatch_elapsed: 0, stopwatch_started_at: null, cut_date: null });
  }, [updateItemStopwatch]);

  const formatStopwatch = useCallback((seconds) => {
    const s = Math.max(0, Math.floor(seconds));
    const hrs = Math.floor(s / 3600);
    const mins = Math.floor((s % 3600) / 60);
    const secs = s % 60;
    if (hrs > 0) return `${hrs}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }, []);

  const activeItems = savedList.filter(i => !i.saved_for_later);
  const savedForLaterItems = savedList.filter(i => i.saved_for_later);

  const headerTitle = listTitle && listTitle !== 'Cut List' ? `Cut List (${listTitle})` : 'Cut List';

  if (savedList.length === 0) {
    return (
      <Card className="bg-white border border-zinc-200 rounded-none shadow-none md:col-span-2 lg:col-span-4" data-testid={`saved-list-card-${listId || 'main'}`}>
        <CardHeader className="p-4 pb-4">
          <CardTitle className="text-lg font-bold text-zinc-400 tracking-tight flex items-center gap-2" style={{ fontFamily: "Chivo, sans-serif" }}>
            <ClipboardList className="w-5 h-5 text-zinc-300" />
            {headerTitle}
            <span className="text-sm font-normal text-zinc-400 ml-1">(0)</span>
          </CardTitle>
        </CardHeader>
      </Card>
    );
  }

  const printList = (mode = 'checked') => {
    const escHtml = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    const modeLabels = { checked: 'Checked Off Components', entire: 'Entire Cut List', later: 'Saved For Later Components' };
    const checkedItems = mode === 'entire' ? activeItems : mode === 'later' ? savedForLaterItems : savedList.filter(i => i.checked);
    if (checkedItems.length === 0) { alert(`Nothing to print — no items in "${modeLabels[mode]}"`); return; }
    const printWindow = window.open('', '_blank');
    const totalStopwatchSecs = checkedItems.filter(i => !i.stopwatch_started_at).reduce((sum, item) => sum + getElapsed(item), 0);
    const totalEstMinutes = checkedItems.reduce((sum, i) => sum + (i.pcs_per_min ? (i.total || 0) / i.pcs_per_min : 0), 0);
    const totalBdFt = checkedItems.reduce((sum, i) => sum + ((i.board_footage || 0) * (i.per_pallet || 0) * (i.pallet_quantity || 0)), 0);
    // Raw board footage from lumber used
    let rawBdFt = 0;
    checkedItems.forEach(item => {
      (item.lumber || []).forEach((l, li) => {
        if (!l?.length || !item.lumber_checked?.[li]) return;
        const t = parseFloat(l.thickness) || 0;
        const w = l.width === 'Random' ? 6 : (parseFloat(l.width) || 6);
        const len = parseFloat(l.length) || 0;
        const mainQty = parseFloat(item.lumber_custom_qty?.[li] || "") || 0;
        if (mainQty > 0) rawBdFt += (mainQty * t * w * len) / 12;
        const extras = item.lumber_extra_used?.[li] || [];
        extras.forEach(ex => {
          const exQty = parseFloat(ex.qty || "") || 0;
          if (exQty > 0) rawBdFt += (exQty * t * w * len) / 12;
        });
      });
    });

    const missingTime = checkedItems.filter(i => !i.pcs_per_min).length;
    const missingBdFt = checkedItems.filter(i => !i.board_footage).length;

    const token = localStorage.getItem('token') || '';
    const saveData = {
      items: checkedItems.map(i => ({ name: i.name, total: i.total, pallet_type_name: i.pallet_type_name })),
      totals: { est_time: formatTime(totalEstMinutes), actual_time: formatStopwatch(totalStopwatchSecs), board_footage: totalBdFt },
    };

    const html = `<!DOCTYPE html><html><head><title>Pallet Cut Calculator - Saved List</title>
<style>
  body { font-family: Arial, sans-serif; padding: 20px; color: #111; }
  h1 { font-size: 20px; margin-bottom: 4px; }
  .date { font-size: 12px; color: #666; margin-bottom: 16px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
  th, td { border: 1px solid #ccc; padding: 6px 10px; text-align: left; font-size: 13px; }
  th { background: #222; color: #fff; font-weight: bold; text-transform: uppercase; font-size: 11px; letter-spacing: 0.5px; }
  tr:nth-child(even) { background: #f9f9f9; }
  .notes { font-size: 11px; color: #555; font-style: italic; margin-top: 2px; }
  .lumber-opt { margin-bottom: 5px; padding-bottom: 4px; border-bottom: 1px dotted #ddd; }
  .lumber-opt:last-child { margin-bottom: 0; padding-bottom: 0; border-bottom: none; }
  .lumber-detail { color: #333; font-size: 12px; }
  .totals { margin-top: 16px; padding: 12px; background: #222; color: #fff; }
  .totals h3 { margin: 0 0 8px; font-size: 14px; text-transform: uppercase; letter-spacing: 1px; }
  .totals p { margin: 4px 0; font-size: 13px; }
  .missing { color: #f44; font-weight: bold; }
  .action-bar { display: flex; gap: 10px; justify-content: center; margin: 20px auto; }
  .action-btn { padding: 12px 32px; border: none; font-size: 15px; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; cursor: pointer; }
  .action-btn:active { opacity: 0.8; }
  .save-print-btn { background: #ea580c; color: #fff; }
  .close-btn { background: #222; color: #fff; }
  @media print { body { padding: 0; } .action-bar { display: none; } }
</style></head><body>
<h1>${escHtml(headerTitle)} — ${modeLabels[mode]}</h1>
<p class="date">${new Date().toLocaleDateString()} - ${checkedItems.length} items</p>
<table>
  <thead><tr><th>Component</th><th>Qty</th><th>Est. Time</th><th>Actual Time</th><th>Dimensions</th><th>Bd Ft</th><th>Lumber</th><th>Skids</th><th>Source</th></tr></thead>
  <tbody>${checkedItems.map(item => `<tr>
    <td>${escHtml(item.name)}${item.notched ? ' <em>(Notched)</em>' : ''}${item.notes ? `<div class="notes">${escHtml(item.notes)}</div>` : ''}</td>
    <td><strong>${item.total.toLocaleString()}</strong></td>
    <td>${item.pcs_per_min > 0 ? formatTime(item.total / item.pcs_per_min) : '—'}</td>
    <td>${formatStopwatch(getElapsed(item))}</td>
    <td>${item.thickness || '—'} x ${item.height || '—'} x ${item.length || '—'}</td>
    <td>${item.board_footage ? (item.board_footage * (item.per_pallet || 0) * (item.pallet_quantity || 0)).toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—'}</td>
    <td>${(() => { const opts = (item.lumber || []).map((l, li) => ({ l, li })).filter(({ l }) => l?.quantity || l?.pcs_per_cant); if (opts.length === 0) return '—'; const compLen = item.length || 0; const origTotal = item.original_total || item.total; return opts.map(({ l, li }) => { const cantIn = (l.length || 0) * 12; const ppc = l.pcs_per_cant || (l.quantity > 0 && origTotal > 0 ? Math.round(origTotal / l.quantity) : 0) || (compLen > 0 ? Math.floor(cantIn / compLen) : 0); const required = ppc > 0 ? Math.round((origTotal / ppc) * 100) / 100 : Math.round((l.quantity || 0) * 100) / 100; const mainUsed = parseFloat(item.lumber_custom_qty?.[li] || "") || 0; const extraUsed = (item.lumber_extra_used?.[li] || []).reduce((s, ex) => s + (parseFloat(ex.qty || "") || 0), 0); const used = Math.round((mainUsed + extraUsed) * 100) / 100; const species = item.lumber_custom_species?.[li] || ''; const dims = `${l.thickness ? `${l.thickness}" x ` : ''}${l.width && l.width !== 'Random' ? `${l.width}" x ` : l.width === 'Random' ? 'Rndm x ' : ''}${l.length}'`; const desc = l.description ? ` ${escHtml(l.description)}` : ''; const sp = species && !(l.description || '').toLowerCase().startsWith(species.toLowerCase()) ? ` ${escHtml(species)}` : ''; return `<div class="lumber-opt"><strong>${escHtml(dims)}</strong>${desc}${sp}${ppc > 0 ? ` (${ppc} pcs/cant)` : ''}<br><span class="lumber-detail">Required: <strong>${required}</strong> cants${used > 0 ? ` &nbsp;&bull;&nbsp; Cants used: <strong>${used}</strong>` : ''}</span></div>`; }).join(''); })()}</td>
    <td>${item.skids != null && item.skids > 0 ? (() => { const w = Math.floor(item.skids); const r = item.boards_per_skid ? Math.round((item.skids - w) * item.boards_per_skid * 100) / 100 : 0; if (w > 0 && r > 0) return `${w} Skid${w > 1 ? 's' : ''} + ${r} Pcs.`; if (w > 0) return `${w} Skid${w > 1 ? 's' : ''}`; if (r > 0) return `${r} Pcs.`; return `${item.skids} Skids`; })() : '—'}</td>
    <td>${item.pallet_type_name}</td>
  </tr>`).join('')}</tbody>
</table>
<div class="totals">
  <h3>Totals</h3>
  ${totalEstMinutes > 0 ? `<p>Estimated Time: <strong>${formatTime(totalEstMinutes)}</strong>${missingTime > 0 ? ` <span class="missing">(${missingTime} missing!)</span>` : ''}</p>` : ''}
  ${totalStopwatchSecs > 0 ? `<p>Actual Time (Checked Components Only): <strong>${formatStopwatch(totalStopwatchSecs)}</strong></p>` : ''}
  ${totalBdFt > 0 ? `<p>Finished Product Board Feet: <strong>${totalBdFt.toLocaleString(undefined, { maximumFractionDigits: 2 })}</strong>${missingBdFt > 0 ? ` <span class="missing">(${missingBdFt} missing!)</span>` : ''}</p>` : ''}
  ${rawBdFt > 0 ? `<p>Raw Board Footage: <strong>${rawBdFt.toLocaleString(undefined, { maximumFractionDigits: 2 })}</strong></p>` : ''}
  ${(() => {
    const cantTotals = {};
    checkedItems.forEach(item => {
      (item.lumber || []).forEach((l, li) => {
        if (!l?.length || !item.lumber_checked?.[li]) return;
        const mainQty = parseFloat(item.lumber_custom_qty?.[li] || "") || 0;
        const mainSpecies = item.lumber_custom_species?.[li] || speciesOrFirstWord(l.description);
        if (mainQty > 0) {
          const key = (l.thickness || '') + '|' + (l.width || '') + '|' + l.length + '|' + mainSpecies;
          if (!cantTotals[key]) cantTotals[key] = { thickness: l.thickness, width: l.width, length: l.length, species: mainSpecies, total: 0 };
          cantTotals[key].total += mainQty;
        }
        const extras = item.lumber_extra_used?.[li] || [];
        extras.forEach(ex => {
          const exQty = parseFloat(ex.qty || "") || 0;
          if (exQty <= 0) return;
          const exSpecies = ex.species || mainSpecies;
          const key = (l.thickness || '') + '|' + (l.width || '') + '|' + l.length + '|' + exSpecies;
          if (!cantTotals[key]) cantTotals[key] = { thickness: l.thickness, width: l.width, length: l.length, species: exSpecies, total: 0 };
          cantTotals[key].total += exQty;
        });
      });
    });
    const entries = Object.values(cantTotals).sort((a, b) => (parseFloat(a.thickness) || 0) - (parseFloat(b.thickness) || 0) || (parseFloat(a.length) || 0) - (parseFloat(b.length) || 0));
    if (entries.length === 0) return '';
    return '<h3 style="margin-top:12px">Lumber Used</h3>' + entries.map(e => '<p>' + Math.round(e.total * 100) / 100 + ' — ' + (e.thickness ? e.thickness + '"' : '') + (e.width && e.width !== 'Random' ? ' x ' + e.width + '"' : e.width === 'Random' ? ' x Rndm' : '') + " x " + e.length + "'" + (e.species ? ' ' + e.species : '') + '</p>').join('');
  })()}
</div>
<div class="action-bar">
  <button class="action-btn save-print-btn" id="savePrintBtn">Save & Print</button>
  <button class="action-btn close-btn" onclick="window.close()">Close</button>
</div>
<script>
  document.getElementById('savePrintBtn').addEventListener('click', function() {
    window.opener.postMessage('save-and-print', '*');
    window.print();
  });
</script>
</body></html>`;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();

    // Listen for save message from print window
    const handleMessage = (e) => {
      if (e.data === 'save-and-print') {
        window.removeEventListener('message', handleMessage);
        axios.post(`${API}/print-history`, { ...saveData, html }).catch(() => {});
      }
    };
    window.addEventListener('message', handleMessage);
  };

  return (
    <>
    <Card
      className="bg-white border border-zinc-200 rounded-none shadow-none md:col-span-2 lg:col-span-4"
      data-testid="saved-list-card"
    >
      <CardHeader className="p-4 sm:p-6 pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle
            className={`text-lg sm:text-xl font-bold text-zinc-900 tracking-tight flex items-center gap-2 ${collapsible ? 'cursor-pointer select-none' : ''}`}
            style={{ fontFamily: "Chivo, sans-serif" }}
            onClick={collapsible ? () => setCollapsed(c => !c) : undefined}
            data-testid={`cutlist-header-${listId || 'main'}`}
          >
            {collapsible && <ChevronRight className={`w-5 h-5 text-zinc-400 transition-transform ${collapsed ? '' : 'rotate-90'}`} />}
            <ClipboardList className="w-5 h-5 text-blue-600" />
            {headerTitle}
            <span className="text-sm font-normal text-zinc-500 ml-1">({activeItems.length})</span>
          </CardTitle>
          {!readOnly && !collapsed && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setReorderMode(prev => !prev)}
                variant="outline"
                size="sm"
                className={`rounded-none h-8 text-xs ${reorderMode ? 'bg-blue-50 border-blue-400 text-blue-700' : 'border-zinc-300 text-zinc-600 hover:bg-zinc-50'}`}
                data-testid="reorder-toggle-btn"
              >
                <GripVertical className="w-3.5 h-3.5 mr-1" />
                Buttons {reorderMode ? 'On' : 'Off'}
              </Button>
              <Button
                onClick={() => setPrintDialog(true)}
                variant="outline"
                size="sm"
                className="rounded-none border-zinc-300 text-zinc-600 hover:bg-zinc-50 h-8 text-xs"
                data-testid="print-list-btn"
              >
                <Printer className="w-3.5 h-3.5 mr-1" />
                Print
              </Button>
              <Button
                onClick={() => setSortConfirm(true)}
                variant="outline"
                size="sm"
                className="rounded-none border-violet-300 text-violet-600 hover:bg-violet-50 h-8 text-xs"
                data-testid="sort-thickness-btn"
              >
                <ArrowUpDown className="w-3.5 h-3.5 mr-1" />
                Sort
              </Button>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setHistChooser(true)}
                variant="outline"
                size="sm"
                className="rounded-none border-zinc-300 text-zinc-600 hover:bg-zinc-50 h-8 text-xs w-fit"
                data-testid="list-history-btn"
              >
                <ChevronRight className="w-3.5 h-3.5 mr-1 hidden" />
                History
              </Button>
              <Button
                onClick={() => setClearListConfirm(listId || true)}
                variant="outline"
                size="sm"
                className="rounded-none border-red-300 text-red-600 hover:bg-red-50 h-8 text-xs w-fit"
                data-testid="clear-list-btn"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                Clear
              </Button>
            </div>
          </div>
          )}
        </div>
      </CardHeader>
      {!collapsed && (
      <CardContent className="p-4 sm:p-6 pt-0">
        <div className="space-y-3">
          {(() => {
            const thicknessOf = (it) => {
              const t = parseFloat(it.thickness);
              return isNaN(t) ? null : t;
            };
            const renderItem = (item, idx) => (
            <CutListItem
              key={item.id}
              item={item} idx={idx} dragIdx={dragIdx} reorderMode={reorderMode}
              tick={item.stopwatch_started_at ? tick : 0}
              formatTime={formatTime} formatStopwatch={formatStopwatch} getElapsed={getElapsed}
              toggleCheck={toggleCheck} removeFromList={removeFromList} moveItem={moveItem}
              toggleSavedForLater={toggleSavedForLater} moveToHistory={moveToHistory}
              updateItemCustomQty={updateItemCustomQty} updateItemStopwatch={updateItemStopwatch}
              startTimer={startTimer} stopTimer={stopTimer} resetTimer={resetTimer}
              editingIdx={editingIdx} setEditingIdx={setEditingIdx}
              customQty={customQty} setCustomQty={setCustomQty}
              editingTimeId={editingTimeId} setEditingTimeId={setEditingTimeId}
              customTime={customTime} setCustomTime={setCustomTime}
              readOnly={readOnly}
            />
            );
            if (!highlightOn) return activeItems.map((item, idx) => renderItem(item, idx));
            const groups = [];
            activeItems.forEach((item, idx) => {
              const t = thicknessOf(item);
              const last = groups[groups.length - 1];
              if (last && last.thickness !== null && t !== null && last.thickness === t) last.items.push({ item, idx });
              else groups.push({ thickness: t, items: [{ item, idx }] });
            });
            return groups.map((g, gi) => {
              if (g.items.length >= 2 && g.thickness !== null) {
                return (
                  <div key={`grp-${g.items[0].item.id}`} className="relative border-2 border-amber-400 bg-amber-50/40 p-1.5 pt-4 space-y-3" data-testid={`thickness-group-${gi}`}>
                    <span className="absolute -top-2.5 left-2 px-1.5 bg-amber-400 text-white text-[10px] font-bold leading-4" data-testid={`thickness-group-label-${gi}`}>
                      {g.thickness}&quot;
                    </span>
                    {g.items.map(({ item, idx }) => renderItem(item, idx))}
                  </div>
                );
              }
              return g.items.map(({ item, idx }) => renderItem(item, idx));
            });
          })()}
        </div>

        {/* List Totals Summary */}
        {activeItems.length > 0 && (() => {
          const estMinutes = activeItems.reduce((sum, i) => sum + (i.pcs_per_min ? (i.total || 0) / i.pcs_per_min : 0), 0);
          const actualSeconds = activeItems.filter(i => i.checked && !i.stopwatch_started_at).reduce((sum, i) => sum + getElapsed(i), 0);
          const actualHours = actualSeconds / 3600;
          const cutListBdFt = activeItems.reduce((sum, i) => sum + ((i.board_footage || 0) * (i.per_pallet || 0) * (i.pallet_quantity || 0)), 0);
          const checkedBdFt = activeItems.filter(i => i.checked).reduce((sum, i) => sum + ((i.board_footage || 0) * (i.per_pallet || 0) * (i.pallet_quantity || 0)), 0);
          const priceBdFt = priceBdFtRaw / 100;
          const priceHour = priceHourRaw / 100;
          const missingBf = activeItems.filter(i => !i.board_footage).length;
          const missingTime = activeItems.filter(i => !i.pcs_per_min).length;
          const missingActual = activeItems.filter(i => i.checked && getElapsed(i) === 0).length;
          const selectedBdFt = bdFtSource === "checked" ? checkedBdFt : bdFtSource === "cutlist" ? cutListBdFt : (parseFloat(customBdFt) || 0);
          const bdFtTotal = priceBdFt > 0 ? selectedBdFt * priceBdFt : 0;
          const estHours = estMinutes / 60;
          const selectedHours = timeSource === "actual" ? actualHours : timeSource === "estimated" ? estHours : (parseFloat(customHours) || 0);
          const selectedSeconds = timeSource === "actual" ? actualSeconds : timeSource === "estimated" ? estMinutes * 60 : (parseFloat(customHours) || 0) * 3600;
          const timeTotal = priceHour > 0 ? selectedHours * priceHour : 0;
          const grandTotal = bdFtTotal + timeTotal;

          return (
        <div className="mt-6 p-4 sm:p-5 bg-zinc-900 text-white" data-testid="saved-list-totals">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-4 font-semibold">Cut List Totals</p>
          <div className="flex flex-wrap gap-6 justify-evenly">
            {activeItems.some(i => i.pcs_per_min) && (() => {
              const missingTime = activeItems.filter(i => !i.pcs_per_min).length;
              return (
              <div className="text-center min-w-[80px]">
                <p className="text-xs text-zinc-500 mb-1">Est. Time {missingTime > 0 && <span className="text-red-400 font-bold">({missingTime} missing!)</span>}</p>
                <p className="text-lg sm:text-xl font-bold text-zinc-100 break-all" style={{ fontFamily: "Chivo, sans-serif" }}>
                  {formatTime(estMinutes)}
                </p>
              </div>
              );
            })()}
            <div className="text-center min-w-[80px]">
              <p className="text-xs text-zinc-500 mb-1">Actual Time (Checked Components Only) {missingActual > 0 && <span className="text-red-400 font-bold">*{missingActual} missing*</span>}</p>
              <p className="text-lg sm:text-xl font-bold text-zinc-100 break-all" style={{ fontFamily: "Chivo, sans-serif" }}>
                {formatStopwatch(actualSeconds)}
              </p>
            </div>
            {activeItems.some(i => i.board_footage) && (() => {
              const checkedMissingBf = activeItems.filter(i => i.checked && !i.board_footage).length;
              return (<>
              <div className="text-center min-w-[80px]">
                <p className="text-xs text-zinc-500 mb-1">Checked Items Bd Ft {checkedMissingBf > 0 && <span className="text-red-400 font-bold">({checkedMissingBf} missing!)</span>}</p>
                <p className="text-lg sm:text-xl font-bold text-zinc-100 break-all" style={{ fontFamily: "Chivo, sans-serif" }}>
                  {checkedBdFt.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </p>
              </div>
              <div className="text-center min-w-[80px]">
                <p className="text-xs text-zinc-500 mb-1">Cut List Bd Ft {missingBf > 0 && <span className="text-red-400 font-bold">({missingBf} missing!)</span>}</p>
                <p className="text-lg sm:text-xl font-bold text-zinc-100 break-all" style={{ fontFamily: "Chivo, sans-serif" }}>
                  {cutListBdFt.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </p>
              </div>
              </>);
            })()}
          </div>

          {/* Pricing - Office page only */}
          {readOnly && (
            <div className="mt-4 pt-4 border-t border-zinc-700">
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-3 font-semibold">Pricing</p>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-500">Use Bd Ft from</span>
                  <select value={bdFtSource} onChange={(e) => setBdFtSource(e.target.value)}
                    className="h-7 text-xs border border-zinc-600 bg-zinc-800 text-white rounded-none focus:border-orange-400 focus:outline-none px-2"
                    data-testid="bdft-source-select">
                    <option value="checked">Checked Items</option>
                    <option value="cutlist">Full Cut List</option>
                    <option value="custom">Custom Amount</option>
                  </select>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-500">
                    Bd Ft ({bdFtSource === 'checked' ? 'Checked' : bdFtSource === 'cutlist' ? 'Cut List' : 'Custom'})
                    {bdFtSource === 'checked' && activeItems.filter(i => i.checked && !i.board_footage).length > 0 && <span className="text-red-400 font-bold ml-1">({activeItems.filter(i => i.checked && !i.board_footage).length} missing!)</span>}
                    {bdFtSource === 'cutlist' && missingBf > 0 && <span className="text-red-400 font-bold ml-1">({missingBf} missing!)</span>}
                  </span>
                  {bdFtSource === 'custom' ? (
                    <input type="number" step="0.01" min="0" value={customBdFt} onChange={(e) => setCustomBdFt(e.target.value)}
                      placeholder="0" className="w-24 h-7 text-right text-sm font-bold border border-zinc-600 bg-zinc-800 text-white focus:border-orange-400 focus:outline-none px-2"
                      style={{ fontFamily: "Chivo, sans-serif" }} data-testid="custom-bdft-input" />
                  ) : (
                    <span className="text-sm font-bold text-zinc-100" style={{ fontFamily: "Chivo, sans-serif" }}>
                      {selectedBdFt.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-500">Price / Bd. Ft.</span>
                  <input
                    type="text" inputMode="numeric"
                    value={`$${priceBdFt.toFixed(2)}`}
                    onKeyDown={(e) => { e.preventDefault(); if (e.key >= '0' && e.key <= '9') setPriceBdFtRaw(p => p * 10 + parseInt(e.key)); else if (e.key === 'Backspace') setPriceBdFtRaw(p => Math.floor(p / 10)); }}
                    onChange={() => {}}
                    className="w-24 h-7 text-right text-sm font-bold border border-zinc-600 bg-zinc-800 text-white focus:border-orange-400 focus:outline-none px-2"
                    style={{ fontFamily: "Chivo, sans-serif", caretColor: "transparent" }}
                    data-testid="cutlist-price-bdft"
                  />
                </div>
                {bdFtTotal > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-zinc-500">Bd. Ft. Total</span>
                    <span className="text-lg font-bold text-zinc-100" style={{ fontFamily: "Chivo, sans-serif" }}>
                      ${bdFtTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-500">Use Time from</span>
                  <select value={timeSource} onChange={(e) => setTimeSource(e.target.value)}
                    className="h-7 text-xs border border-zinc-600 bg-zinc-800 text-white rounded-none focus:border-orange-400 focus:outline-none px-2"
                    data-testid="time-source-select">
                    <option value="actual">Actual (Checked)</option>
                    <option value="estimated">Estimated</option>
                    <option value="custom">Custom Amount</option>
                  </select>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-500">
                    Time ({timeSource === 'actual' ? 'Actual' : timeSource === 'estimated' ? 'Est.' : 'Custom'})
                    {timeSource === 'actual' && missingActual > 0 && <span className="text-red-400 font-bold ml-1">({missingActual} missing!)</span>}
                    {timeSource === 'estimated' && missingTime > 0 && <span className="text-red-400 font-bold ml-1">({missingTime} missing!)</span>}
                  </span>
                  {timeSource === 'custom' ? (
                    <input type="number" step="0.01" min="0" value={customHours} onChange={(e) => setCustomHours(e.target.value)}
                      placeholder="Hours" className="w-24 h-7 text-right text-sm font-bold border border-zinc-600 bg-zinc-800 text-white focus:border-orange-400 focus:outline-none px-2"
                      style={{ fontFamily: "Chivo, sans-serif" }} data-testid="custom-hours-input" />
                  ) : (
                    <span className="text-sm font-bold text-zinc-100" style={{ fontFamily: "Chivo, sans-serif" }}>
                      {formatStopwatch(selectedSeconds)}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-500">Price / Hour</span>
                  <input
                    type="text" inputMode="numeric"
                    value={`$${priceHour.toFixed(2)}`}
                    onKeyDown={(e) => { e.preventDefault(); if (e.key >= '0' && e.key <= '9') setPriceHourRaw(p => p * 10 + parseInt(e.key)); else if (e.key === 'Backspace') setPriceHourRaw(p => Math.floor(p / 10)); }}
                    onChange={() => {}}
                    className="w-24 h-7 text-right text-sm font-bold border border-zinc-600 bg-zinc-800 text-white focus:border-orange-400 focus:outline-none px-2"
                    style={{ fontFamily: "Chivo, sans-serif", caretColor: "transparent" }}
                    data-testid="cutlist-price-hour"
                  />
                </div>
                {timeTotal > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-zinc-500">Time Total ({formatStopwatch(selectedSeconds)})</span>
                    <span className="text-lg font-bold text-zinc-100" style={{ fontFamily: "Chivo, sans-serif" }}>
                      ${timeTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                {grandTotal > 0 && (
                  <div className="flex items-center justify-between pt-3 border-t border-zinc-600">
                    <span className="text-xs text-zinc-400 font-bold uppercase tracking-wider">Grand Total</span>
                    <span className="text-2xl font-bold text-orange-400" style={{ fontFamily: "Chivo, sans-serif" }}>
                      ${grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Checked lumber used summary */}
          {(() => {
            const cantTotals = {};
            activeItems.forEach(item => {
              (item.lumber || []).forEach((l, li) => {
                if (!l?.length || !item.lumber_checked?.[li]) return;
                // Main cants used entry
                const used = parseFloat(item.lumber_custom_qty?.[li] || "") || 0;
                const mainSpecies = item.lumber_custom_species?.[li] || speciesOrFirstWord(l.description);
                if (used > 0) {
                  const key = `${l.thickness || ''}|${l.width || ''}|${l.length}|${mainSpecies}`;
                  cantTotals[key] = (cantTotals[key] || { thickness: l.thickness, width: l.width, length: l.length, species: mainSpecies, total: 0 });
                  cantTotals[key].total += used;
                }
                // Extra entries
                const extras = item.lumber_extra_used?.[li] || [];
                extras.forEach(ex => {
                  const exUsed = parseFloat(ex.qty || "") || 0;
                  if (exUsed <= 0) return;
                  const exSpecies = ex.species || mainSpecies;
                  const key = `${l.thickness || ''}|${l.width || ''}|${l.length}|${exSpecies}`;
                  cantTotals[key] = (cantTotals[key] || { thickness: l.thickness, width: l.width, length: l.length, species: exSpecies, total: 0 });
                  cantTotals[key].total += exUsed;
                });
              });
            });
            const entries = Object.values(cantTotals).sort((a, b) => {
              const tDiff = (parseFloat(a.thickness) || 0) - (parseFloat(b.thickness) || 0);
              if (tDiff !== 0) return tDiff;
              const lDiff = (a.length || 0) - (b.length || 0);
              if (lDiff !== 0) return lDiff;
              return (a.species || '').localeCompare(b.species || '');
            });
            if (entries.length === 0) return null;
            return (
              <div className="mt-4 pt-4 border-t border-zinc-700">
                <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-3 font-semibold">Lumber Used</p>
                <div className="space-y-2">
                  {entries.map((entry, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-lg font-bold text-zinc-100 min-w-[60px]" style={{ fontFamily: "Chivo, sans-serif" }}>
                        {Math.round(entry.total * 100) / 100}
                      </span>
                      <span className="text-zinc-500">——</span>
                      <span className="text-sm text-zinc-300">
                        {entry.thickness ? `${entry.thickness}"` : ''}{entry.width && entry.width !== 'Random' ? ` x ${entry.width}"` : entry.width === 'Random' ? ' x Rndm' : ''} x {entry.length}&apos;{entry.species ? ` ${entry.species}` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
        );
        })()}

        {/* Saved for Later Section */}
        {savedForLaterItems.length > 0 && (
          <div className="mt-6" data-testid="saved-for-later-section">
            <div className="flex items-center gap-2 mb-3">
              <Bookmark className="w-4 h-4 text-zinc-400" />
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400" style={{ fontFamily: "Chivo, sans-serif" }}>
                Saved for Later
              </p>
              <span className="text-xs text-zinc-400">({savedForLaterItems.length})</span>
            </div>
            <div className="space-y-3">
              {savedForLaterItems.map((item, idx) => (
                <CutListItem
                  key={item.id}
                  item={item} idx={idx} dragIdx={dragIdx} reorderMode={reorderMode}
                  tick={item.stopwatch_started_at ? tick : 0}
                  formatTime={formatTime} formatStopwatch={formatStopwatch} getElapsed={getElapsed}
                  toggleCheck={toggleCheck} removeFromList={removeFromList} moveItem={moveItem}
                  toggleSavedForLater={toggleSavedForLater} moveToHistory={moveToHistory}
                  updateItemCustomQty={updateItemCustomQty} updateItemStopwatch={updateItemStopwatch}
                  startTimer={startTimer} stopTimer={stopTimer} resetTimer={resetTimer}
                  editingIdx={editingIdx} setEditingIdx={setEditingIdx}
                  customQty={customQty} setCustomQty={setCustomQty}
                  editingTimeId={editingTimeId} setEditingTimeId={setEditingTimeId}
                  customTime={customTime} setCustomTime={setCustomTime}
                  readOnly={readOnly}
                />
              ))}
            </div>
          </div>
        )}
      </CardContent>
      )}
    </Card>

    <AlertDialog open={printDialog} onOpenChange={setPrintDialog}>
      <AlertDialogContent className="bg-white border border-zinc-200 rounded-none" data-testid="print-options-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>What would you like to print?</AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-zinc-500">
            Choose which components to include in the printout.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2 py-2">
          <button onClick={() => { setPrintDialog(false); printList('checked'); }}
            className="w-full text-left px-4 py-3 border border-zinc-200 hover:bg-orange-50 hover:border-orange-300 transition-colors text-sm"
            data-testid="print-checked-btn">
            <span className="font-bold text-zinc-800">Checked Off Components</span>
            <span className="text-xs text-zinc-400 ml-2">({savedList.filter(i => i.checked).length})</span>
          </button>
          <button onClick={() => { setPrintDialog(false); printList('entire'); }}
            className="w-full text-left px-4 py-3 border border-zinc-200 hover:bg-orange-50 hover:border-orange-300 transition-colors text-sm"
            data-testid="print-entire-btn">
            <span className="font-bold text-zinc-800">Entire Cut List</span>
            <span className="text-xs text-zinc-400 ml-2">({activeItems.length})</span>
          </button>
          <button onClick={() => { setPrintDialog(false); printList('later'); }}
            className="w-full text-left px-4 py-3 border border-zinc-200 hover:bg-orange-50 hover:border-orange-300 transition-colors text-sm"
            data-testid="print-later-btn">
            <span className="font-bold text-zinc-800">Saved For Later Components</span>
            <span className="text-xs text-zinc-400 ml-2">({savedForLaterItems.length})</span>
          </button>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none w-full" data-testid="print-cancel-btn">Cancel</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <AlertDialog open={sortConfirm} onOpenChange={setSortConfirm}>
      <AlertDialogContent className="bg-white border border-zinc-200 rounded-none" data-testid="sort-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Sort Cut List</AlertDialogTitle>
          <AlertDialogDescription className="text-sm text-zinc-500">
            Choose how to sort. Your current order will be replaced.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2 py-2">
          <button onClick={() => { sortByThickness('thickness-asc'); setSortConfirm(false); }}
            className="w-full text-left px-4 py-3 border border-zinc-200 hover:bg-orange-50 hover:border-orange-300 transition-colors text-sm"
            data-testid="sort-thickness-asc">
            <span className="font-bold text-zinc-800">Thickness — Thinnest to Thickest</span>
          </button>
          <button onClick={() => { sortByThickness('thickness-desc'); setSortConfirm(false); }}
            className="w-full text-left px-4 py-3 border border-zinc-200 hover:bg-orange-50 hover:border-orange-300 transition-colors text-sm"
            data-testid="sort-thickness-desc">
            <span className="font-bold text-zinc-800">Thickness — Thickest to Thinnest</span>
          </button>
          <button onClick={() => { sortByThickness('width-asc'); setSortConfirm(false); }}
            className="w-full text-left px-4 py-3 border border-zinc-200 hover:bg-orange-50 hover:border-orange-300 transition-colors text-sm"
            data-testid="sort-width-asc">
            <span className="font-bold text-zinc-800">Width — Narrowest to Widest</span>
          </button>
          <button onClick={() => { sortByThickness('width-desc'); setSortConfirm(false); }}
            className="w-full text-left px-4 py-3 border border-zinc-200 hover:bg-orange-50 hover:border-orange-300 transition-colors text-sm"
            data-testid="sort-width-desc">
            <span className="font-bold text-zinc-800">Width — Widest to Narrowest</span>
          </button>
          <button onClick={() => { sortByThickness('length-asc'); setSortConfirm(false); }}
            className="w-full text-left px-4 py-3 border border-zinc-200 hover:bg-orange-50 hover:border-orange-300 transition-colors text-sm"
            data-testid="sort-length-asc">
            <span className="font-bold text-zinc-800">Length — Shortest to Longest</span>
          </button>
          <button onClick={() => { sortByThickness('length-desc'); setSortConfirm(false); }}
            className="w-full text-left px-4 py-3 border border-zinc-200 hover:bg-orange-50 hover:border-orange-300 transition-colors text-sm"
            data-testid="sort-length-desc">
            <span className="font-bold text-zinc-800">Length — Longest to Shortest</span>
          </button>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none w-full">Cancel</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* History chooser */}
    <AlertDialog open={histChooser} onOpenChange={setHistChooser}>
      <AlertDialogContent className="bg-white border border-zinc-200 rounded-none max-w-sm" data-testid="list-history-chooser">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>
            History{listTitle ? ` — ${listTitle}` : ''}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-600">Which history do you want to see for this cut list?</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2 py-2">
          <button onClick={() => { setHistChooser(false); onShowHistory && onShowHistory('monthly'); }}
            className="w-full text-left px-4 py-3 border border-zinc-200 hover:bg-orange-50 hover:border-orange-300 transition-colors text-sm"
            data-testid="list-history-monthly">
            <span className="font-bold text-zinc-800">Monthly</span>
            <span className="block text-xs text-zinc-400">Cut components grouped by month</span>
          </button>
          <button onClick={() => { setHistChooser(false); onShowHistory && onShowHistory('components'); }}
            className="w-full text-left px-4 py-3 border border-zinc-200 hover:bg-orange-50 hover:border-orange-300 transition-colors text-sm"
            data-testid="list-history-components">
            <span className="font-bold text-zinc-800">Components</span>
            <span className="block text-xs text-zinc-400">Completed cut list components</span>
          </button>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none w-full">Cancel</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
